import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useScreenVisibility } from '../context/ScreenVisibilityContext';
import { Permission } from '../types';

interface RouteGuardProps {
  children: React.ReactNode;
  requiredPermission?: keyof Permission;
  allowedRoles?: string[];
}

const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  requiredPermission,
  allowedRoles
}) => {
  const { isAuthenticated, currentUser, hasPermission } = useAuth();
  const { isRouteBlocked } = useScreenVisibility();
  const location = useLocation();

  // If not authenticated, redirect to login (handled by App component)
  if (!isAuthenticated || !currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Screen visibility check — supersedes role-based rules
  if (isRouteBlocked(location.pathname)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles && !allowedRoles.includes(currentUser.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check permission-based access if requiredPermission is specified
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default RouteGuard;
